import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SettingsLayout from "@/components/SettingsLayout";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import {
  CreditCard, CheckCircle2, ArrowRight, Ban, Clock3, Plus, Building2,
  ExternalLink, Loader2, Gauge,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Phase C2 follow-on — Billing page wired to the live Stripe flow.
 *
 *   • Plan card "Choose <plan>" buttons → POST /api/subscriptions/checkout-session
 *     → redirect to Stripe-hosted Checkout.
 *   • "Manage subscription" → POST /api/subscriptions/portal-session
 *     → redirect to Stripe-hosted Customer Portal.
 *   • On return: parses ?checkout=success|cancelled from the URL,
 *     surfaces a toast, refetches /me + /usage, clears the query string.
 */
function PlanCard({ plan, current, pending, onSelect, working }) {
  // Phase B2 — pricing PRODUCTS stay GBP regardless of viewer locale; the
  // formatter renders the GBP symbol correctly in any locale (en-US → "£12.00").
  const { formatCurrency } = useLocale();
  const isCurrent = current === plan.code;
  const isPending = pending === plan.code;
  return (
    <div
      className={`eb-card p-8 relative flex flex-col gap-4 ${isCurrent ? "border-orange-500 ring-2 ring-orange-200 scale-[1.02]" : "border-navy-100 hover:border-navy-300"} transition-all duration-200`}
      data-testid={`plan-${plan.code}`}
    >
      {isCurrent && (
        <span className="absolute -top-3 left-8 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          Current plan
        </span>
      )}
      {isPending && (
        <span className="absolute -top-3 left-8 bg-warning text-navy-900 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          Awaiting payment
        </span>
      )}
      <div>
        <h3 className="text-xl font-semibold">{plan.name}</h3>
        <div className="flex items-baseline gap-1 mt-2">
          <span className="text-3xl font-bold" data-testid={`plan-${plan.code}-price`}>
            {formatCurrency(plan.price_gbp * 100, "GBP")}
          </span>
          <span className="text-sm text-navy-500">/ month</span>
        </div>
      </div>
      <ul className="space-y-2 text-sm text-navy-700 flex-1">
        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" /> {plan.site_limit} site{plan.site_limit > 1 ? "s" : ""}</li>
        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" /> {plan.unlimited_visits ? "Unlimited visits" : `${plan.visits_per_month} visits / month`}</li>
        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" /> {plan.kiosk_limit} kiosk{plan.kiosk_limit > 1 ? "s" : ""}</li>
      </ul>
      <button
        onClick={() => onSelect(plan)}
        disabled={isCurrent || working}
        className={isCurrent ? "btn-outline w-full opacity-60 cursor-not-allowed"
                              : working ? "btn-accent w-full opacity-70 cursor-wait"
                                         : "btn-accent w-full"}
        data-testid={`select-plan-${plan.code}`}
      >
        {isCurrent ? "Current"
                    : working ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                              : <>Start 14-day trial <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}


export default function Billing() {
  // Phase B2 — all date / currency renders honour the active locale.
  const { formatDate, formatDateTime, formatCurrency } = useLocale();
  const nav = useNavigate();
  const loc = useLocation();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);   // which plan card is in flight
  const [portalWorking, setPortalWorking] = useState(false);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s, u, o] = await Promise.all([
        api.get("/subscriptions/plans"),
        api.get("/subscriptions/me"),
        api.get("/subscriptions/usage").catch(() => ({ data: null })),
        api.get("/orgs/me"),
      ]);
      setPlans(p.data);
      setSub(s.data);
      setUsage(u.data);
      setOrg(o.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase C2 — handle Stripe Checkout return. The success_url Stripe was
  // configured with carries `?checkout=success&session_id={CHECKOUT_SESSION_ID}`.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const status = params.get("checkout");
    if (!status) return;
    if (status === "success") {
      toast.success("Subscription created — your 14-day trial has started.");
      // Refetch so the new plan + usage land without manual reload. The
      // webhook may not have arrived yet — UI will catch up on the next
      // poll (or operator refresh).
      load();
    } else if (status === "cancelled") {
      toast.message("Checkout cancelled — no charges made.");
    }
    // Clear the query string so refresh doesn't re-toast.
    nav(loc.pathname, { replace: true });
  }, [loc.search, loc.pathname, nav, load]);

  const selectPlan = async (plan) => {
    // Plan codes from /subscriptions/plans map 1:1 to plan_id sent in checkout-session.
    setBusyPlan(plan.code);
    try {
      const { data } = await api.post("/subscriptions/checkout-session",
                                       { plan_id: plan.code });
      // For both mock and live modes the server returns an absolute URL we
      // can navigate to. For mock, the URL points back at /billing?mock=true.
      if (data?.url) {
        window.location.href = data.url;
        return;   // browser leaving — no need to reset state
      }
      toast.error("Checkout session created but no redirect URL was returned.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (typeof det === "string" && det.includes("price_id")) {
        toast.error("Stripe catalogue not yet seeded for this plan. Run stripe_setup_products.py on production.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    setPortalWorking(true);
    try {
      const { data } = await api.post("/subscriptions/portal-session", {
        return_url: window.location.href,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Portal session created but no redirect URL was returned.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "no_stripe_customer") {
        toast.message("Start your subscription first via one of the plans above.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setPortalWorking(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription?")) return;
    setWorking(true);
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Subscription cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const addSite = async () => {
    if (!window.confirm("Add an additional site for £15/month?")) return;
    setWorking(true);
    try {
      const { data } = await api.post("/subscriptions/add-site");
      if (data.mode === "live") {
        toast.success("Stripe checkout opening…");
        if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        toast.success("Extra site added (+£15/mo)");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const cancelExtraSite = async () => {
    if (!window.confirm("Cancel the most recent additional site at the end of the current period?")) return;
    setWorking(true);
    try {
      const { data } = await api.delete("/subscriptions/extra-site");
      toast.success(`Scheduled removal at ${formatDate(data.scheduled_removal_at)}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const hasSubscription = !!(sub && sub.plan && sub.status && sub.status !== "cancelled");

  return (
    <SettingsLayout>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="eb-overline mb-1">Subscription</div>
          <h1 className="text-3xl font-semibold" data-testid="billing-heading">Plans &amp; billing</h1>
          <p className="text-sm text-navy-500 mt-1">
            Choose a plan to start your 14-day free trial. Cards are charged only after the trial ends.
          </p>
        </div>
      </div>

      {/* Current status */}
      {sub && (
        <div className="eb-card p-6 mb-6 flex flex-wrap items-center justify-between gap-4"
             data-testid="current-sub-card">
          <div>
            <div className="text-xs text-navy-500 uppercase tracking-wider">Current subscription</div>
            <div className="mt-1 flex items-center gap-3">
              <span className="text-2xl font-bold capitalize">{sub.plan}</span>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                sub.status === "active" ? "bg-success/15 text-success" :
                sub.status === "trialing" ? "bg-orange-100 text-orange-700" :
                sub.status === "past_due" ? "bg-warning/15 text-warning-700" :
                sub.status === "cancelled" || sub.status === "canceled" ? "bg-navy-100 text-navy-600" :
                "bg-warning/15 text-warning"
              }`} data-testid="sub-status-badge">
                {sub.status}
              </span>
            </div>
            {sub.status === "trialing" && sub.trial_ends_at && (
              <div className="text-xs text-navy-500 mt-2 flex items-center gap-1.5">
                <Clock3 className="w-3 h-3" /> Trial ends {formatDate(sub.trial_ends_at)}
              </div>
            )}
            {sub.status === "past_due" && (
              <div className="text-xs text-warning-700 mt-2">
                Payment failed — open the Customer Portal below to update your card.
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-accent" onClick={openPortal} disabled={portalWorking}
                    data-testid="manage-subscription-btn">
              {portalWorking ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
                              : <><ExternalLink className="w-4 h-4" /> Manage subscription</>}
            </button>
            {sub.status !== "cancelled" && sub.status !== "canceled" && (
              <button className="btn-outline" onClick={cancel} disabled={working}
                      data-testid="cancel-sub-btn">
                <Ban className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Solo-plan visit quota chip — shows here too for parity with Live Dashboard. */}
      {usage && usage.plan_id === "solo" && (
        <div className="eb-card p-4 mb-6 flex items-center gap-3"
             data-testid="solo-quota-banner">
          <Gauge className={`w-5 h-5 ${
            usage.visits_this_month / usage.visits_limit >= 0.9 ? "text-danger-600"
              : usage.visits_this_month / usage.visits_limit >= 0.7 ? "text-warning-600"
                : "text-success-600"
          }`} />
          <div className="flex-1">
            <div className="text-sm font-semibold">
              {usage.visits_this_month} / {usage.visits_limit} visits this month
            </div>
            <div className="text-xs text-navy-500">
              Solo plan caps at 100 sign-ins per calendar month. Upgrade for unlimited.
            </div>
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
        {loading ? (
          <p className="text-navy-500 text-sm">Loading…</p>
        ) : (
          plans.map((p) => (
            <PlanCard key={p.code} plan={p}
                       current={sub?.plan_id || sub?.plan}
                       pending={sub?.pending_plan}
                       onSelect={selectPlan}
                       working={busyPlan === p.code} />
          ))
        )}
      </div>

      {/* Additional sites — £15/mo each */}
      {hasSubscription && org && (
        <div className="eb-card p-6 mt-8" data-testid="additional-sites-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="eb-overline mb-1">Additional sites · {formatCurrency(1500, "GBP")} / month each</div>
              <h3 className="text-lg font-semibold inline-flex items-center gap-2">
                <Building2 className="w-5 h-5 text-orange-500" />
                Extra sites purchased: <span className="text-orange-600">{org.extra_sites_purchased || 0}</span>
              </h3>
              <p className="text-xs text-navy-500 mt-1">
                Goes beyond your plan's included sites. Cancel any time — the extra slot stays
                live until the end of the current billing period.
                {org.extra_sites_pending_removal_at && (
                  <span className="block text-warning-600 mt-1">
                    Scheduled removal at {formatDateTime(org.extra_sites_pending_removal_at)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={addSite} disabled={working}
                className="btn-accent" data-testid="add-site-btn">
                <Plus className="w-4 h-4" /> Add a site
              </button>
              {(org.extra_sites_purchased || 0) > 0 && !org.extra_sites_pending_removal_at && (
                <button onClick={cancelExtraSite} disabled={working}
                  className="btn-outline" data-testid="cancel-extra-site-btn">
                  Cancel one
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Portal CTA — for orgs that haven't subscribed yet. */}
      {!hasSubscription && (
        <div className="mt-10 eb-card p-6" data-testid="portal-card-not-subscribed">
          <div className="eb-overline mb-1">Customer portal</div>
          <h3 className="text-lg font-semibold inline-flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-orange-500" /> Manage cards &amp; invoices
          </h3>
          <p className="text-sm text-navy-600 mt-2 max-w-xl">
            After you start a subscription above, this page links to a
            Stripe-hosted portal where you can update your card, view
            invoices, change plan, or cancel.
          </p>
          <p className="mt-3 text-[11px] text-navy-400" data-testid="licence-footnote">
            Licence terms apply. Pricing is in GBP; additional sites are billed per active site at the end of each month.
          </p>
        </div>
      )}
    </SettingsLayout>
  );
}
